import { useEffect, useId, useState } from 'react';
import { useApi, useRouteRef } from '@backstage/frontend-plugin-api';
import type { Entity } from '@backstage/catalog-model';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import scaffolderPlugin from '@backstage/plugin-scaffolder/alpha';
import {
  Alert,
  Button,
  Card,
  Container,
  Link,
  SearchField,
  Skeleton,
  Text,
  ToggleButton,
  ToggleButtonGroup,
} from '@backstage/ui';
import { useAdminCheck } from '../home/useIsAdmin';
import { useAppTypeDescriptors } from '../shared/templateRoles';
import { tasksRouteRef } from '../tasks/routeRef';
import {
  CATEGORIES,
  isAdminCard,
  matchesQuery,
  showSoonCard,
  templateCards,
  templateToolbar,
  type Audience,
  type TemplateCard,
  type TemplateCategory,
} from './cardPresentation';
import { TemplateLogo } from './TemplateLogo';
import { TemplateTag } from './TemplateTag';

type TemplatesState =
  | { status: 'loading' }
  | { status: 'ready'; entities: Entity[] }
  | { status: 'error'; message: string };

/** Shown, and announced, when no template answers the filters. */
export const NO_MATCH = 'Aucun modèle ne correspond.';

/** The chip of every category, first of the row. */
export const ALL_CATEGORIES = 'Tous';

type CategoryFilter = typeof ALL_CATEGORIES | TemplateCategory;

function isCategoryFilter(value: unknown): value is CategoryFilter {
  return (
    value === ALL_CATEGORIES || CATEGORIES.some(category => category === value)
  );
}

/**
 * One template, a compact tile (mockup « D1 », 2026-10-19): its logo, its
 * title and one line of description, its tag at the right end — replaced by
 * the action's words when the tile is hovered or focused (styles.css). The
 * whole tile is a link to its form (BUI `Card` with `href`, whose overlay
 * link carries the name « <title> — <action> »), the action's words being
 * text, not a second link.
 */
function TemplateTile(props: { card: TemplateCard; href?: string }) {
  const { card, href } = props;
  const content = (
    <>
      <TemplateLogo logo={card.logo} />
      <span className="waadoo-create-tile__text">
        <Text
          as="h3"
          variant="body-medium"
          weight="bold"
          className="waadoo-create-tile__title"
        >
          {card.title}
        </Text>
        <Text
          as="span"
          variant="body-small"
          color="secondary"
          className="waadoo-create-tile__description"
        >
          {card.description}
        </Text>
      </span>
      <span className="waadoo-create-tile__end">
        {card.tag && <TemplateTag tag={card.tag} />}
        <span className="waadoo-create-tile__go" aria-hidden="true">
          {card.action} →
        </span>
      </span>
    </>
  );
  if (!href) {
    return <Card className="waadoo-create-tile">{content}</Card>;
  }
  return (
    <Card
      className="waadoo-create-tile"
      href={href}
      label={`${card.title} — ${card.action}`}
    >
      {content}
    </Card>
  );
}

/** The dashed « Bientôt » tile at the end of the grid: words, not a link. */
function SoonTile() {
  return (
    <Card className="waadoo-create-tile" data-soon="">
      <TemplateLogo logo="soon" />
      <span className="waadoo-create-tile__text">
        <Text
          as="h3"
          variant="body-medium"
          weight="bold"
          color="secondary"
          className="waadoo-create-tile__title"
        >
          Bientôt : Moodle, Nextcloud, n8n…
        </Text>
        <Text
          as="span"
          variant="body-small"
          color="secondary"
          className="waadoo-create-tile__description"
        >
          Une application à proposer ? Parlez-en à un administrateur.
        </Text>
      </span>
    </Card>
  );
}

/**
 * The search field and the category chips above the tiles, when the list
 * has them (`templateToolbar`, cardPresentation.ts): the chips, `categories`
 * after « Tous », may be none.
 *
 * The chips are one single-choice group (react-aria: a radio group, its
 * chips radios, the arrow keys moving between them), drawn as separate
 * pill toggles (« M1 », `.waadoo-pill-button.waadoo-pill-toggle`).
 */
function TemplateToolbar(props: {
  categories: TemplateCategory[];
  query: string;
  onQuery: (query: string) => void;
  category: CategoryFilter;
  onCategory: (category: CategoryFilter) => void;
}) {
  const { categories, query, onQuery, category, onCategory } = props;
  return (
    <div className="waadoo-create-toolbar">
      <div role="search" className="waadoo-pill-search waadoo-create-search">
        <SearchField
          aria-label="Rechercher un modèle"
          size="small"
          placeholder="Rechercher un modèle…"
          value={query}
          onChange={onQuery}
        />
      </div>
      {categories.length > 0 && (
        <ToggleButtonGroup
          aria-label="Catégorie"
          className="waadoo-create-chips"
          selectionMode="single"
          disallowEmptySelection
          selectedKeys={[category]}
          onSelectionChange={keys => {
            const [selected] = keys;
            if (isCategoryFilter(selected)) onCategory(selected);
          }}
        >
          {[ALL_CATEGORIES, ...categories].map(chip => (
            <ToggleButton
              key={chip}
              id={chip}
              size="small"
              className="waadoo-pill-button waadoo-pill-toggle"
            >
              {chip}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}
    </div>
  );
}

/**
 * The template list of « Créer » (mockup « D1 », 2026-10-19; first « U2 »
 * and « A », 2026-10-15), in place of upstream's catalog-filtered list: one
 * tile per template the catalog returns to the caller — the permission
 * policy decides which, the list adds no filter of its own. Presentation per
 * template (title, category, logo, tag): `cardPresentation.ts`.
 *
 * A toolbar (search, category chips) when the list is long enough to need
 * one (`templateToolbar`); the tiles in a grid, the « Bientôt » tile last; the « Administration »
 * templates in a section of their own below — only an admin's catalog
 * returns any.
 *
 * Waits for the admin check as well as the catalog: the tag of the project
 * template and the « Bientôt » tile depend on it. A failed check counts as
 * a non-admin, like everywhere else (`useIsAdmin.ts`). Waits for the
 * AppTypes too: their creation templates' titles (`templateCards`) — so no
 * tile changes under the reader's eyes; when they cannot be read, or have
 * not answered within `DESCRIPTORS_WAIT_MS` (`useAppTypeDescriptors`), every
 * template under the catalog's title. The historical templates are left out
 * either way (`templateCards`).
 */
export function TemplateCards() {
  const catalogApi = useApi(catalogApiRef);
  const check = useAdminCheck();
  const descriptors = useAppTypeDescriptors();
  const templateRoute = useRouteRef(scaffolderPlugin.routes.selectedTemplate);
  const tasksRoute = useRouteRef(tasksRouteRef);
  const [state, setState] = useState<TemplatesState>({ status: 'loading' });
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<CategoryFilter>(ALL_CATEGORIES);
  const adminHeadingId = useId();

  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });
    catalogApi
      .getEntities({
        filter: { kind: 'Template' },
        // What a tile shows: a whole Template (the form, its steps) is
        // heavy, `odoo-project` about 1 200 lines.
        fields: [
          'kind',
          'metadata.name',
          'metadata.namespace',
          'metadata.title',
          'metadata.description',
          // The « admin » tag (`ADMIN_ONLY_LABEL`) and `HIDDEN_LABEL`.
          'metadata.labels',
          // The category and the logo of a template the app does not know
          // (`CATEGORY_ANNOTATION`, `LOGO_ANNOTATION`).
          'metadata.annotations',
        ],
      })
      .then(
        response => {
          if (active) setState({ status: 'ready', entities: response.items });
        },
        (error: unknown) => {
          if (active) {
            setState({
              status: 'error',
              message: error instanceof Error ? error.message : String(error),
            });
          }
        },
      );
    return () => {
      active = false;
    };
  }, [catalogApi]);

  const audience: Audience = check === 'admin' ? 'admin' : 'member';

  const tile = (card: TemplateCard) => (
    <TemplateTile
      key={card.ref}
      card={card}
      href={templateRoute?.({
        namespace: card.namespace,
        templateName: card.name,
      })}
    />
  );

  const renderBody = () => {
    if (state.status === 'error') {
      return (
        <Alert
          status="danger"
          icon
          title="Impossible de lire les modèles"
          description={state.message}
        />
      );
    }
    if (
      state.status === 'loading' ||
      check === 'pending' ||
      descriptors.status === 'loading'
    ) {
      return (
        <div className="waadoo-create-grid" aria-busy="true">
          <Skeleton height="3.75rem" />
          <Skeleton height="3.75rem" />
          <Skeleton height="3.75rem" />
        </div>
      );
    }
    const cards = templateCards(
      state.entities,
      audience,
      descriptors.status === 'ready' ? descriptors.items : [],
    );
    if (cards.length === 0) {
      return (
        <div className="waadoo-create-empty" role="status">
          <Text variant="body-medium" weight="bold">
            Aucun modèle disponible pour l’instant.
          </Text>
        </div>
      );
    }

    const toolbar = templateToolbar(cards);
    const chips = toolbar?.chips ?? [];
    // A chip whose category the list no longer has, or no chips at all:
    // every category.
    const activeCategory =
      category !== ALL_CATEGORIES && chips.includes(category)
        ? category
        : ALL_CATEGORIES;
    const activeQuery = toolbar ? query : '';
    const filtered = activeCategory !== ALL_CATEGORIES || activeQuery !== '';
    const shown = cards.filter(
      card =>
        (activeCategory === ALL_CATEGORIES ||
          card.category === activeCategory) &&
        matchesQuery(card, activeQuery),
    );
    const main = shown.filter(card => !isAdminCard(card));
    const admin = shown.filter(isAdminCard);
    const soon = !filtered && showSoonCard(cards.length, audience);
    const reset = () => {
      setQuery('');
      setCategory(ALL_CATEGORIES);
    };

    let announcement = '';
    if (toolbar) {
      announcement =
        shown.length === 0
          ? NO_MATCH
          : `${shown.length} modèle${shown.length > 1 ? 's' : ''}`;
    }

    return (
      <>
        {/* The page's h1 is « Créer »: the list's own heading, for the
            outline, the admin section's h2 beside it. */}
        <h2 className="waadoo-visually-hidden">Modèles</h2>
        {/* Mounted with the list, so that a change of what it holds is
            announced: the number of templates shown, or that none matches
            — the visible message below is not a live region of its own. */}
        <p role="status" className="waadoo-visually-hidden">
          {announcement}
        </p>
        {toolbar && (
          <TemplateToolbar
            categories={chips}
            query={query}
            onQuery={setQuery}
            category={activeCategory}
            onCategory={setCategory}
          />
        )}
        {shown.length === 0 && (
          <div className="waadoo-create-empty">
            <Text variant="body-medium" weight="bold">
              {NO_MATCH}
            </Text>
            <span>
              <Button size="small" variant="secondary" onPress={reset}>
                Réinitialiser les filtres
              </Button>
            </span>
          </div>
        )}
        {(main.length > 0 || soon) && (
          <div className="waadoo-create-grid">
            {main.map(tile)}
            {soon && <SoonTile />}
          </div>
        )}
        {admin.length > 0 && (
          <section
            className="waadoo-create-section"
            aria-labelledby={adminHeadingId}
          >
            <h2 id={adminHeadingId} className="waadoo-create-section__label">
              <span className="waadoo-create-section__name">
                Administration
              </span>{' '}
              · réservé aux administrateurs
            </h2>
            <div className="waadoo-create-grid">{admin.map(tile)}</div>
          </section>
        )}
      </>
    );
  };

  const tasksHref = tasksRoute?.();
  return (
    <Container>
      <div className="waadoo-create">
        {renderBody()}
        {audience === 'member' && check !== 'pending' && tasksHref && (
          <Text as="p" variant="body-small" color="secondary">
            Vos créations en cours et passées :{' '}
            <Link href={tasksHref} weight="bold">
              Tâches <span aria-hidden="true">→</span>
            </Link>
          </Text>
        )}
      </div>
    </Container>
  );
}
